import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import ConfirmDialog from '../components/ui/ConfirmDialog'

// Alert Context
const AlertContext = createContext(null)

// Alert types
export const ALERT_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error', 
  WARNING: 'warning',
  INFO: 'info'
}

// Alert Provider Component
export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([])

  // Add alert function
  const addAlert = useCallback((message, type = ALERT_TYPES.INFO, duration = 5000) => {
    const id = Date.now() + Math.random()
    const alert = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    }

    setAlerts(prev => [...prev, alert])

    // Auto-remove alert after duration
    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id)
      }, duration)
    }

    return id
  }, [])

  // Remove alert function
  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id))
  }, [])

  // Clear all alerts
  const clearAllAlerts = useCallback(() => {
    setAlerts([])
  }, [])

  // Convenience methods for different alert types
  const showSuccess = useCallback((message, duration) => {
    return addAlert(message, ALERT_TYPES.SUCCESS, duration)
  }, [addAlert])

  const showError = useCallback((message, duration = 8000) => {
    return addAlert(message, ALERT_TYPES.ERROR, duration)
  }, [addAlert])

  const showWarning = useCallback((message, duration) => {
    return addAlert(message, ALERT_TYPES.WARNING, duration)
  }, [addAlert])

  const showInfo = useCallback((message, duration) => {
    return addAlert(message, ALERT_TYPES.INFO, duration)
  }, [addAlert])

  // Confirmation dialog: `await showConfirm(title, message, confirmText,
  // cancelText, type)` resolves true on confirm and false on cancel, Esc or
  // a click outside. type: 'warning' | 'danger' | 'success' | 'info', and
  // 'destructive' is accepted as an alias for 'danger'.
  const [confirmState, setConfirmState] = useState(null)
  const confirmResolveRef = useRef(null)

  const settleConfirm = useCallback((result) => {
    const resolve = confirmResolveRef.current
    confirmResolveRef.current = null
    // Keep the last content while the dialog closes.
    setConfirmState(prev => (prev ? { ...prev, open: false } : prev))
    if (resolve) resolve(result)
  }, [])

  const showConfirm = useCallback((title, message, confirmText, cancelText, type = 'warning') => {
    // A new request answers any confirmation still waiting with "no".
    if (confirmResolveRef.current) confirmResolveRef.current(false)
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve
      setConfirmState({
        open: true,
        title,
        message,
        confirmText,
        cancelText,
        type: type === 'destructive' ? 'danger' : type,
      })
    })
  }, [])

  // Context value
  const contextValue = {
    alerts,
    addAlert,
    removeAlert,
    clearAllAlerts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm
  }

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        isOpen={Boolean(confirmState?.open)}
        onClose={() => settleConfirm(false)}
        onConfirm={() => settleConfirm(true)}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        type={confirmState?.type}
      />
    </AlertContext.Provider>
  )
}

// Custom hook to use alerts
export const useAlerts = () => {
  const context = useContext(AlertContext)
  
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider')
  }
  
  return context
}
